import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, Copy, Search, Loader2, X, ChevronLeft, ChevronRight, Image as ImageIcon, Star, Palette, DollarSign, Hash, Layers, Minus, FolderTree, Circle, Package, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { ImageUploader } from '../../components/ImageUploader';
import { getCloudinaryThumbnail } from '../../lib/cloudinaryUtils';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationEllipsis,
} from '../../components/ui/pagination';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'Única'];
const DEFAULT_COLORS = ['Negro', 'Blanco', 'Dorado', 'Verde', 'Verde Aguacate', 'Coral', 'Natural', 'Rojo', 'Azul', 'Rosa'];

export default function AdminProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Gallery modal states
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentGalleryImageIndex, setCurrentGalleryImageIndex] = useState(0);
  
  // Image manager modal states
  const [imageManagerProduct, setImageManagerProduct] = useState(null);
  const [imageManagerImages, setImageManagerImages] = useState([]);
  const [savingImages, setSavingImages] = useState(false);

  // Product pending deletion (drives the confirm dialog)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Inline rename of a custom size/color pill: { field, value, vIdx } + draft text
  const [editingOption, setEditingOption] = useState(null);
  const [optionDraft, setOptionDraft] = useState('');

  // Dynamic options for sizes and colors
  const [availableSizes, setAvailableSizes] = useState(DEFAULT_SIZES);
  const [availableColors, setAvailableColors] = useState(DEFAULT_COLORS);

  const [customFilter, setCustomFilter] = useState('all');
  const [searchParams, setSearchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    name: '', description: '', price: '', compare_price: '', cost_price: '',
    sku: '', category_id: '', status: 'active', featured: false, is_custom: false,
    tryon_image: '', images: [], variants: []
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, customFilter]);

  useEffect(() => {
    fetchProducts();
  }, [searchQuery, currentPage, customFilter]);

  // Keyboard navigation for gallery
  useEffect(() => {
    if (!isGalleryOpen || !selectedProduct?.images) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentGalleryImageIndex(prev => 
          prev === 0 ? selectedProduct.images.length - 1 : prev - 1
        );
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentGalleryImageIndex(prev => 
          (prev + 1) % selectedProduct.images.length
        );
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsGalleryOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGalleryOpen, selectedProduct]);

  // Open edit modal from query param (e.g. from analytics page)
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;

    const openProductById = async () => {
      try {
        const res = await axios.get(`${API}/products/${editId}`);
        openModal(res.data);
      } catch (error) {
        toast.error('No se pudo cargar el producto');
      }
      // Clean the query param so it doesn't re-trigger
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    };

    openProductById();
  }, [searchParams]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: itemsPerPage.toString(),
        is_custom: customFilter
      });
      if (searchQuery) params.append('search', searchQuery);

      const countParams = new URLSearchParams({ is_custom: customFilter });
      if (searchQuery) countParams.append('search', searchQuery);

      const [productsRes, countRes] = await Promise.all([
        axios.get(`${API}/products?${params}`),
        axios.get(`${API}/products/count?${countParams}`)
      ]);
      
      setProducts(productsRes.data);
      setTotalProducts(countRes.data.count);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      const variants = product.variants || [];
      // Seed the size/color pill lists with this product's existing values so they
      // show up as selectable (and highlighted) pills instead of being hidden in
      // the "Otro" input. Without this, any custom color/size looks un-editable.
      setAvailableSizes([...new Set([...DEFAULT_SIZES, ...variants.map(v => v.size).filter(Boolean)])]);
      setAvailableColors([...new Set([...DEFAULT_COLORS, ...variants.map(v => v.color).filter(Boolean)])]);
      setFormData({
        name: product.name, description: product.description || '',
        price: product.price.toString(), compare_price: product.compare_price?.toString() || '',
        cost_price: product.cost_price?.toString() || '', sku: product.sku || '',
        category_id: product.category_id || '', status: product.status,
        featured: product.featured, is_custom: product.is_custom || false,
        tryon_image: product.tryon_image || '', images: product.images || [],
        variants
      });
    } else {
      setEditingProduct(null);
      setAvailableSizes(DEFAULT_SIZES);
      setAvailableColors(DEFAULT_COLORS);
      setFormData({
        name: '', description: '', price: '', compare_price: '', cost_price: '',
        sku: '', category_id: '', status: 'active', featured: false, is_custom: false,
        tryon_enabled: false, images: [], variants: [{ size: 'M', color: 'Negro', stock_quantity: 10 }]
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      toast.error('Nombre y precio son requeridos');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        compare_price: formData.compare_price ? parseFloat(formData.compare_price) : null,
        cost_price: formData.cost_price ? parseFloat(formData.cost_price) : null,
        // Trim stray whitespace so colors/sizes stay clean (" Azul " -> "Azul")
        variants: (formData.variants || []).map(v => ({
          ...v,
          size: typeof v.size === 'string' ? v.size.trim() : v.size,
          color: typeof v.color === 'string' ? v.color.trim() : v.color,
        })),
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload);
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/products`, payload);
        toast.success('Producto creado');
      }

      setIsModalOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/products/${deleteTarget.id}`);
      toast.success('Producto eliminado');
      setDeleteTarget(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/products/${id}/duplicate`);
      toast.success('Producto duplicado');
      fetchProducts();
    } catch (error) {
      toast.error('Error al duplicar');
    }
  };

  const openImageManager = (product) => {
    setImageManagerProduct(product);
    setImageManagerImages(product.images || []);
  };

  const saveImages = async () => {
    if (!imageManagerProduct) return;
    setSavingImages(true);
    try {
      await axios.patch(`${API}/products/${imageManagerProduct.id}/images`, { images: imageManagerImages });
      toast.success('Imágenes actualizadas');
      setImageManagerProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error('Error al guardar imágenes');
    } finally {
      setSavingImages(false);
    }
  };

  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, { size: 'M', color: 'Negro', stock_quantity: 0 }]
    }));
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === index ? { ...v, [field]: value } : v)
    }));
  };

  // Register a freshly typed size/color as a pill — only once the user finishes
  // typing (onBlur). Doing this on every keystroke would clear the input mid-word.
  const commitCustomOption = (field, raw) => {
    const value = (raw || '').trim();
    if (!value) return;
    if (field === 'size' && !availableSizes.includes(value)) {
      setAvailableSizes(prev => [...prev, value]);
    }
    if (field === 'color' && !availableColors.includes(value)) {
      setAvailableColors(prev => [...prev, value]);
    }
  };

  // ---- Manage existing size/color options (custom pills only) ----
  const startEditOption = (field, value, vIdx) => {
    setEditingOption({ field, value, vIdx });
    setOptionDraft(value);
  };

  const cancelEditOption = () => setEditingOption(null);

  const commitEditOption = () => {
    if (!editingOption) return;
    const { field, value: oldV } = editingOption;
    const newV = (optionDraft || '').trim();
    setEditingOption(null);
    if (!newV || newV === oldV) return;
    // Rename the option in the pill list (dedupe if the new name already exists)...
    const setList = field === 'size' ? setAvailableSizes : setAvailableColors;
    setList(prev => [...new Set(prev.map(x => (x === oldV ? newV : x)))]);
    // ...and across every variant that used the old value.
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map(v => (v[field] === oldV ? { ...v, [field]: newV } : v)),
    }));
  };

  const deleteOption = (field, value) => {
    const setList = field === 'size' ? setAvailableSizes : setAvailableColors;
    setList(prev => prev.filter(x => x !== value));
  };

  // Renders one option pill with edit/delete actions for custom values, or an
  // inline rename input when this exact pill (in this variant row) is being edited.
  const renderOptionPill = (field, val, i) => {
    const variant = formData.variants[i];
    const isSelected = variant[field] === val;
    const isCustom = field === 'size' ? !DEFAULT_SIZES.includes(val) : !DEFAULT_COLORS.includes(val);
    const isEditing = editingOption && editingOption.field === field && editingOption.value === val && editingOption.vIdx === i;

    if (isEditing) {
      return (
        <span key={`edit-${val}`} className="inline-flex items-center gap-1 flex-shrink-0">
          <Input
            autoFocus
            value={optionDraft}
            onChange={(e) => setOptionDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitEditOption(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelEditOption(); }
            }}
            className="w-28 h-8 bg-black border-gold/50 text-white text-xs rounded-full px-3"
          />
          <button type="button" onClick={commitEditOption} title="Guardar" className="p-1 rounded-full text-gold hover:bg-white/10">
            <Check className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={cancelEditOption} title="Cancelar" className="p-1 rounded-full text-white/40 hover:bg-white/10">
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      );
    }

    return (
      <span
        key={val}
        className={`inline-flex items-center rounded-full border text-xs font-medium transition-all flex-shrink-0 ${isSelected ? 'bg-gold/20 border-gold text-gold' : 'bg-white/5 border-white/10 text-white/50 hover:border-white/30'}`}
      >
        <button
          type="button"
          onClick={() => updateVariant(i, field, val)}
          className={`py-1.5 whitespace-nowrap ${isCustom ? 'pl-3 pr-1' : 'px-3'}`}
        >
          {val}
        </button>
        {isCustom && (
          <span className="flex items-center gap-0.5 pl-0.5 pr-1.5">
            <button type="button" onClick={() => startEditOption(field, val, i)} title="Renombrar (en todas las variantes)" className="p-0.5 rounded-full hover:text-gold hover:bg-white/10">
              <Pencil className="w-3 h-3" />
            </button>
            <button type="button" onClick={() => deleteOption(field, val)} title="Quitar de la lista" className="p-0.5 rounded-full hover:text-red-400 hover:bg-white/10">
              <X className="w-3 h-3" />
            </button>
          </span>
        )}
      </span>
    );
  };

  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  // Gallery functions
  const openGallery = (product) => {
    if (!product.images || product.images.length === 0) {
      toast.info('Este producto no tiene imágenes');
      return;
    }
    setSelectedProduct(product);
    setCurrentGalleryImageIndex(0);
    setIsGalleryOpen(true);
  };

  const nextGalleryImage = () => {
    if (selectedProduct?.images) {
      setCurrentGalleryImageIndex(prev => 
        (prev + 1) % selectedProduct.images.length
      );
    }
  };

  const prevGalleryImage = () => {
    if (selectedProduct?.images) {
      setCurrentGalleryImageIndex(prev => 
        prev === 0 ? selectedProduct.images.length - 1 : prev - 1
      );
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-products-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif text-white">Productos</h1>
          <p className="text-white/60 text-sm">{totalProducts} productos totales</p>
        </div>

        <Button onClick={() => openModal()} className="btn-gold-solid w-full sm:w-auto" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Producto
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar productos..."
            className="pl-10 bg-[#121212] border-white/10 text-white"
            data-testid="product-search"
          />
        </div>
        <Select value={customFilter} onValueChange={setCustomFilter}>
          <SelectTrigger className="w-[160px] bg-[#121212] border-white/10 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/20">
            <SelectItem value="all" className="text-white">Todos</SelectItem>
            <SelectItem value="false" className="text-white">Tienda</SelectItem>
            <SelectItem value="true" className="text-white">Personalizados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Products List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <p className="text-center text-white/40 py-12">No hay productos</p>
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="md:hidden space-y-3">
            {products.map((product) => (
              <div key={product.id} className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden" data-testid={`product-row-${product.id}`}>
                <div className="flex gap-3 p-3">
                  {/* Thumbnail */}
                  <div
                    className="w-16 h-20 flex-shrink-0 bg-white/5 overflow-hidden rounded relative cursor-pointer"
                    onClick={() => openGallery(product)}
                  >
                    {product.images?.[0] ? (
                      <>
                        <img src={getCloudinaryThumbnail(product.images[0])} alt={product.name} className="w-full h-full object-cover" />
                        {product.images.length > 1 && (
                          <div className="absolute top-0.5 right-0.5 bg-black/70 text-white text-[10px] px-1 rounded">
                            {product.images.length}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20 text-[10px]">Sin img</div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm leading-tight truncate">{product.name}</p>
                    <p className="text-white/40 text-xs mt-0.5">{product.category_name || 'Sin categoría'}</p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-gold font-semibold text-sm">${product.price.toFixed(2)}</span>
                      {product.compare_price && (
                        <span className="text-white/30 line-through text-xs">${product.compare_price.toFixed(2)}</span>
                      )}
                      <span className={`text-xs font-medium ${product.total_stock <= 5 ? 'text-red-400' : 'text-white/50'}`}>
                        Stock: {product.total_stock}
                      </span>
                    </div>
                    <div className="mt-1.5 flex gap-1">
                      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                        product.status === 'active' ? 'bg-green-500/20 text-green-400' :
                        product.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {product.status === 'active' ? 'Activo' : product.status === 'draft' ? 'Borrador' : 'Agotado'}
                      </span>
                      {product.is_custom && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-500/20 text-purple-400">
                          Personalizado
                        </span>
                      )}
                      {product.tryon_image && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-cyan-500/20 text-cyan-400">
                          Probador
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions row */}
                <div className="flex border-t border-white/5 divide-x divide-white/5">
                  <button onClick={() => openImageManager(product)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-white/40 hover:text-emerald-400 hover:bg-white/5 transition-colors text-xs">
                    <ImageIcon className="w-3.5 h-3.5" />
                    <span>Fotos</span>
                  </button>
                  <button onClick={() => openModal(product)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-white/40 hover:text-gold hover:bg-white/5 transition-colors text-xs" data-testid={`edit-product-${product.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                    <span>Editar</span>
                  </button>
                  <button onClick={() => handleDuplicate(product.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-white/40 hover:text-blue-400 hover:bg-white/5 transition-colors text-xs">
                    <Copy className="w-3.5 h-3.5" />
                    <span>Copiar</span>
                  </button>
                  <button onClick={() => setDeleteTarget(product)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-white/40 hover:text-red-400 hover:bg-white/5 transition-colors text-xs" data-testid={`delete-product-mobile-${product.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Borrar</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="py-4 px-4 text-left text-white/60 text-sm font-medium">Producto</th>
                  <th className="py-4 px-4 text-left text-white/60 text-sm font-medium">Categoría</th>
                  <th className="py-4 px-4 text-right text-white/60 text-sm font-medium">Precio</th>
                  <th className="py-4 px-4 text-center text-white/60 text-sm font-medium">Stock</th>
                  <th className="py-4 px-4 text-center text-white/60 text-sm font-medium">Estado</th>
                  <th className="py-4 px-4 text-right text-white/60 text-sm font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`product-row-${product.id}`}>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-24 h-32 bg-white/5 flex-shrink-0 overflow-hidden rounded border border-white/10 shadow-sm relative group cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => openGallery(product)}
                        >
                          {product.images?.[0] ? (
                            <>
                              <img src={getCloudinaryThumbnail(product.images[0])} alt={product.name} className="w-full h-full object-cover" />
                              {product.images.length > 1 && (
                                <div className="absolute top-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                                  {product.images.length}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">
                              Sin imagen
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{product.name}</p>
                          <p className="text-white/40 text-xs">{product.sku || 'Sin SKU'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-white/60">{product.category_name || '-'}</span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className="text-gold font-semibold">${product.price.toFixed(2)}</span>
                      {product.compare_price && (
                        <span className="block text-white/40 text-sm line-through">${product.compare_price.toFixed(2)}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`font-medium ${product.total_stock <= 5 ? 'text-red-500' : 'text-white'}`}>
                        {product.total_stock}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`px-2 py-1 text-xs font-medium ${
                          product.status === 'active' ? 'bg-green-500/20 text-green-500' :
                          product.status === 'draft' ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-red-500/20 text-red-500'
                        }`}>
                          {product.status === 'active' ? 'Activo' : product.status === 'draft' ? 'Borrador' : 'Agotado'}
                        </span>
                        {product.is_custom && (
                          <span className="px-2 py-1 text-xs font-medium bg-purple-500/20 text-purple-400">
                            Personalizado
                          </span>
                        )}
                        {product.tryon_image && (
                          <span className="px-2 py-1 text-xs font-medium bg-cyan-500/20 text-cyan-400">
                            Probador
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openImageManager(product)} className="p-2 text-white/40 hover:text-emerald-400 transition-colors" title="Gestionar imágenes">
                          <ImageIcon className="w-4 h-4" />
                        </button>
                        <button onClick={() => openModal(product)} className="p-2 text-white/40 hover:text-gold transition-colors" data-testid={`edit-product-${product.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDuplicate(product.id)} className="p-2 text-white/40 hover:text-blue-500 transition-colors">
                          <Copy className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteTarget(product)} className="p-2 text-white/40 hover:text-red-500 transition-colors" data-testid={`delete-product-${product.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {!loading && totalProducts > itemsPerPage && (
        <div className="flex flex-col items-center gap-4">
          <Pagination>
            <PaginationContent className="gap-2">
              <PaginationItem>
                <PaginationLink
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage(prev => Math.max(1, prev - 1));
                  }}
                  size="default"
                  className={`${currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'} flex items-center gap-1`}
                  href="#"
                  aria-label="Ir a página anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span>Anterior</span>
                </PaginationLink>
              </PaginationItem>
              
              {(() => {
                const totalPages = Math.ceil(totalProducts / itemsPerPage);
                const pages = [];
                const maxVisiblePages = 5;
                
                if (totalPages <= maxVisiblePages) {
                  // Show all pages if total is small
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(
                      <PaginationItem key={i}>
                        <PaginationLink
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(i);
                          }}
                          isActive={currentPage === i}
                          size="default"
                          className="cursor-pointer min-w-[2.5rem]"
                          href="#"
                        >
                          {i}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }
                } else {
                  // Show first page, ellipsis, current page area, ellipsis, last page
                  pages.push(
                    <PaginationItem key={1}>
                      <PaginationLink
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(1);
                        }}
                        isActive={currentPage === 1}
                        size="default"
                        className="cursor-pointer min-w-[2.5rem]"
                        href="#"
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>
                  );
                  
                  if (currentPage > 3) {
                    pages.push(
                      <PaginationItem key="ellipsis1">
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  
                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);
                  
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <PaginationItem key={i}>
                        <PaginationLink
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(i);
                          }}
                          isActive={currentPage === i}
                          size="default"
                          className="cursor-pointer min-w-[2.5rem]"
                          href="#"
                        >
                          {i}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }
                  
                  if (currentPage < totalPages - 2) {
                    pages.push(
                      <PaginationItem key="ellipsis2">
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  
                  pages.push(
                    <PaginationItem key={totalPages}>
                      <PaginationLink
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(totalPages);
                        }}
                        isActive={currentPage === totalPages}
                        size="default"
                        className="cursor-pointer min-w-[2.5rem]"
                        href="#"
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
                
                return pages;
              })()}
              
              <PaginationItem>
                <PaginationLink
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentPage(prev => Math.min(Math.ceil(totalProducts / itemsPerPage), prev + 1));
                  }}
                  size="default"
                  className={`${currentPage >= Math.ceil(totalProducts / itemsPerPage) ? 'pointer-events-none opacity-50' : 'cursor-pointer'} flex items-center gap-1`}
                  href="#"
                  aria-label="Ir a página siguiente"
                >
                  <span>Siguiente</span>
                  <ChevronRight className="h-4 w-4" />
                </PaginationLink>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
          
          <p className="text-white/60 text-sm">
            Página {currentPage} de {Math.ceil(totalProducts / itemsPerPage)} 
            {totalProducts > 0 && ` (Mostrando ${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, totalProducts)} de ${totalProducts})`}
          </p>
        </div>
      )}

      {/* Product Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white sm:max-w-4xl max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto p-0">
          <div className="px-4 sm:px-6 pt-5 sm:pt-6 pb-3 sm:pb-4 pr-12 sm:pr-14">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl sm:text-2xl">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </DialogTitle>
              <p className="text-white/40 text-sm mt-1">
                {editingProduct ? 'Modifica los datos del producto' : 'Completa la información del producto'}
              </p>
            </DialogHeader>
          </div>

          <form onSubmit={handleSubmit} className="px-4 sm:px-6">
            {/* Section 1: Información Básica */}
            <div className="pb-6 mb-6 border-b border-white/5">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <div className="w-9 h-9 rounded-lg bg-gold/20 flex items-center justify-center flex-shrink-0">
                  <Package className="w-4 h-4 text-gold" />
                </div>
                <h3 className="font-semibold text-base text-white">Información Básica</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-white/60 text-sm">Nombre del Producto *</label>
                    <span className="text-white/30 text-xs">{formData.name.length}/100</span>
                  </div>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.slice(0, 100) })}
                    required
                    maxLength={100}
                    className="bg-black border-white/20 text-white text-lg"
                    placeholder="Nombre del producto"
                  />
                </div>

                <div>
                  <label className="text-white/60 text-sm mb-1 block">Descripción</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="bg-black border-white/20 text-white"
                    placeholder="Describe tu producto..."
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, featured: !formData.featured })}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border text-sm transition-all ${formData.featured ? 'bg-gold/20 border-gold text-gold' : 'bg-white/5 border-white/10 text-white/50 hover:border-white/30'}`}
                  >
                    <Star className="w-4 h-4" /> Destacado
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, is_custom: !formData.is_custom })}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border text-sm transition-all ${formData.is_custom ? 'bg-gold/20 border-gold text-gold' : 'bg-white/5 border-white/10 text-white/50 hover:border-white/30'}`}
                  >
                    <Palette className="w-4 h-4" /> Personalizado
                  </button>
                </div>
              </div>
            </div>

            {/* Section 2: Precios y Clasificación */}
            <div className="pb-6 mb-6 border-b border-white/5">
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <div className="w-9 h-9 rounded-lg bg-gold/20 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-4 h-4 text-gold" />
                </div>
                <h3 className="font-semibold text-base text-white">Precios y Clasificación</h3>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-white/60 text-sm mb-1 block">Precio de Venta *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        required
                        className="bg-black border-white/20 text-white pl-7"
                        placeholder="89.99"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1 block">Precio Anterior</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.compare_price}
                        onChange={(e) => setFormData({ ...formData, compare_price: e.target.value })}
                        className="bg-black border-white/20 text-white pl-7"
                        placeholder="120.00"
                      />
                    </div>
                    <p className="text-white/30 text-xs mt-1">Se mostrará tachado</p>
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1 block">Costo</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.cost_price}
                        onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
                        className="bg-black border-white/20 text-white pl-7"
                        placeholder="35.00"
                      />
                    </div>
                    <p className="text-white/30 text-xs mt-1">Para calcular margen</p>
                  </div>
                </div>

                {formData.price && formData.cost_price && parseFloat(formData.price) > 0 && parseFloat(formData.cost_price) > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-white/40">Margen:</span>
                    {(() => {
                      const margin = parseFloat(formData.price) - parseFloat(formData.cost_price);
                      const marginPct = (margin / parseFloat(formData.price)) * 100;
                      const color = marginPct > 30 ? 'text-green-400' : marginPct > 15 ? 'text-yellow-400' : 'text-red-400';
                      return (
                        <span className={color}>
                          ${margin.toFixed(2)} ({marginPct.toFixed(1)}%)
                        </span>
                      );
                    })()}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-white/60 text-sm mb-1 flex items-center gap-1.5">
                      <Hash className="w-3.5 h-3.5" /> SKU / Código
                    </label>
                    <Input
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="bg-black border-white/20 text-white"
                      placeholder="BIK-TRO-001"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1 flex items-center gap-1.5">
                      <FolderTree className="w-3.5 h-3.5" /> Categoría
                    </label>
                    <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v })}>
                      <SelectTrigger className="bg-black border-white/20 text-white">
                        <SelectValue placeholder="Seleccionar categoría" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/20">
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-white/60 text-sm mb-1 flex items-center gap-1.5">
                      <Circle className={`w-3.5 h-3.5 ${formData.status === 'active' ? 'text-green-400' : formData.status === 'draft' ? 'text-yellow-400' : 'text-red-400'}`} /> Estado
                    </label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger className="bg-black border-white/20 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/20">
                        <SelectItem value="active" className="text-white">Activo</SelectItem>
                        <SelectItem value="draft" className="text-white">Borrador</SelectItem>
                        <SelectItem value="out_of_stock" className="text-white">Agotado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Imágenes */}
            <div className="pb-6 mb-6 border-b border-white/5">
              <div className="flex items-start sm:items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-lg bg-gold/20 flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-4 h-4 text-gold" />
                </div>
                <div>
                  <h3 className="font-semibold text-base text-white">Imágenes</h3>
                  <p className="text-white/40 text-xs sm:text-sm">Sube hasta 10 imágenes. La primera será la principal.</p>
                </div>
              </div>
              <p className="text-white/40 text-xs mb-4 ml-12">{formData.images.length} de 10</p>
              <ImageUploader
                images={formData.images}
                onImagesChange={(imgs) => setFormData({ ...formData, images: imgs })}
                maxImages={10}
                folder="davocado/products"
                tryonImage={formData.tryon_image}
                onTryonImageChange={(img) => setFormData({ ...formData, tryon_image: img })}
              />
            </div>

            {/* Section 4: Variantes */}
            <div className="pb-6 mb-6">
              <div className="mb-5 space-y-3">
                <div className="flex items-start sm:items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gold/20 flex items-center justify-center flex-shrink-0">
                    <Layers className="w-4 h-4 text-gold" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-base text-white">Variantes de Inventario</h3>
                    <p className="text-white/40 text-xs sm:text-sm">Tallas, colores y stock disponible</p>
                  </div>
                </div>
                <button type="button" onClick={addVariant} className="w-full sm:w-auto text-gold text-sm hover:text-gold-hover flex items-center justify-center sm:justify-start gap-1 py-2 sm:py-0 border border-gold/30 sm:border-0 rounded-xl sm:rounded-none">
                  <Plus className="w-4 h-4" /> Agregar Variante
                </button>
              </div>

              <div className="space-y-3">
                <AnimatePresence initial={false}>
                  {formData.variants.map((variant, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="bg-[#121212] border border-white/10 rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-4">
                        {/* Header: Variant number + delete */}
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-xs font-medium">Variante {i + 1}</span>
                          {formData.variants.length > 1 && (
                            <button type="button" onClick={() => removeVariant(i)} className="p-1.5 text-white/30 hover:text-red-400 transition-colors">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* Size pills — horizontal scroll on mobile */}
                        <div>
                          <span className="text-white/40 text-xs uppercase tracking-wider mb-2 block">Talla</span>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {availableSizes.map((s) => renderOptionPill('size', s, i))}
                            <Input
                              value={!availableSizes.includes(variant.size) ? variant.size : ''}
                              onChange={(e) => updateVariant(i, 'size', e.target.value)}
                              onBlur={(e) => commitCustomOption('size', e.target.value)}
                              placeholder="Otra"
                              className="w-28 h-8 bg-black border-white/20 text-white text-xs rounded-full px-3 flex-shrink-0"
                            />
                          </div>
                        </div>

                        {/* Color pills — horizontal scroll on mobile */}
                        <div>
                          <span className="text-white/40 text-xs uppercase tracking-wider mb-2 block">Color</span>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {availableColors.map((c) => renderOptionPill('color', c, i))}
                            <Input
                              value={!availableColors.includes(variant.color) ? variant.color : ''}
                              onChange={(e) => updateVariant(i, 'color', e.target.value)}
                              onBlur={(e) => commitCustomOption('color', e.target.value)}
                              placeholder="Otro"
                              className="w-28 h-8 bg-black border-white/20 text-white text-xs rounded-full px-3 flex-shrink-0"
                            />
                          </div>
                        </div>

                        {/* Stock stepper */}
                        <div className="flex items-center gap-3 pt-1 border-t border-white/5">
                          <span className="text-white/40 text-xs uppercase tracking-wider">Stock</span>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => updateVariant(i, 'stock_quantity', Math.max(0, variant.stock_quantity - 1))}
                              className="w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <Input
                              type="number"
                              min="0"
                              value={variant.stock_quantity}
                              onChange={(e) => updateVariant(i, 'stock_quantity', parseInt(e.target.value) || 0)}
                              className="w-16 h-9 sm:h-8 text-center text-white font-semibold text-lg bg-transparent border-white/10"
                            />
                            <button
                              type="button"
                              onClick={() => updateVariant(i, 'stock_quantity', variant.stock_quantity + 1)}
                              className="w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {/* Variants summary */}
              {formData.variants.length > 0 && (
                <p className="text-white/60 text-sm mt-4">
                  {formData.variants.length} variante{formData.variants.length !== 1 ? 's' : ''} &bull; {formData.variants.reduce((sum, v) => sum + (v.stock_quantity || 0), 0)} unidades totales
                </p>
              )}
            </div>

            {/* Sticky footer */}
            <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-white/10 p-3 sm:p-4 -mx-4 sm:-mx-6 px-4 sm:px-6 space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between">
              <div className="text-sm text-white/40 hidden sm:block">
                {editingProduct && (
                  <span>{editingProduct.name} &bull; {formData.variants.reduce((sum, v) => sum + (v.stock_quantity || 0), 0)} unidades</span>
                )}
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1 sm:flex-none border-white/20 text-white hover:bg-white/5 h-11 sm:h-9">
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="flex-1 sm:flex-none btn-gold-solid h-11 sm:h-9">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingProduct ? 'Guardar Cambios' : 'Crear Producto')}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Image Manager Modal */}
      <Dialog open={!!imageManagerProduct} onOpenChange={(open) => !open && setImageManagerProduct(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white sm:max-w-2xl max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Imágenes — {imageManagerProduct?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <ImageUploader
              images={imageManagerImages}
              onImagesChange={setImageManagerImages}
              maxImages={10}
            />
            <div className="flex justify-end gap-3 pt-2 border-t border-white/10">
              <Button
                variant="outline"
                onClick={() => setImageManagerProduct(null)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancelar
              </Button>
              <Button
                onClick={saveImages}
                disabled={savingImages}
                className="bg-gold text-black hover:bg-gold/90"
              >
                {savingImages ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar imágenes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Gallery Modal */}
      <Dialog open={isGalleryOpen} onOpenChange={setIsGalleryOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white sm:max-w-5xl max-h-[100dvh] sm:max-h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle className="font-serif text-xl">
              {selectedProduct?.name || 'Galería de Imágenes'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedProduct?.images && selectedProduct.images.length > 0 && (
            <div className="px-6 pb-6">
              {/* Main Image */}
              <div className="relative bg-[#121212] rounded-lg overflow-hidden mb-4">
                <div className="relative aspect-[3/4] max-h-[60vh] flex items-center justify-center">
                  <motion.img
                    key={currentGalleryImageIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    src={selectedProduct.images[currentGalleryImageIndex]}
                    alt={`${selectedProduct.name} - Imagen ${currentGalleryImageIndex + 1}`}
                    className="max-w-full max-h-full object-contain"
                  />
                  
                  {/* Navigation Arrows */}
                  {selectedProduct.images.length > 1 && (
                    <>
                      <button
                        onClick={prevGalleryImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/70 text-white hover:bg-black/90 rounded-full transition-colors"
                        aria-label="Imagen anterior"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={nextGalleryImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/70 text-white hover:bg-black/90 rounded-full transition-colors"
                        aria-label="Imagen siguiente"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}
                  
                  {/* Image Counter */}
                  {selectedProduct.images.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                      {currentGalleryImageIndex + 1} / {selectedProduct.images.length}
                    </div>
                  )}
                </div>
              </div>

              {/* Thumbnails */}
              {selectedProduct.images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {selectedProduct.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentGalleryImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-24 overflow-hidden rounded border-2 transition-all bg-[#121212] ${
                        index === currentGalleryImageIndex 
                          ? 'border-gold scale-105' 
                          : 'border-white/20 hover:border-white/40'
                      }`}
                      aria-label={`Ver imagen ${index + 1}`}
                    >
                      <img
                        src={img}
                        alt={`${selectedProduct.name} - Miniatura ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — reliable in-app dialog (replaces native window.confirm) */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl">¿Eliminar producto?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Vas a eliminar <span className="text-white font-medium">{deleteTarget?.name}</span>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="border-white/20 text-white hover:bg-white/5">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
